"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";
import { useActiveStore } from "@/store/activeStore";
import { Skeleton } from "@/components/ui/Skeleton";
import { FadeIn } from "@/components/ui/FadeIn";

/**
 * 내 매장 미니뷰 (사용자 결정 2026-05-08, 2순위).
 *
 * 본인 매장 핵심 KPI 3 카드 압축:
 *   이번 주 매출 (지난 주 대비 ±N%)
 *   이번 주 TOP 메뉴
 *   재고 알림 (안전재고 미달 카운트)
 *
 * 4 분기 UX (1순위 GapAnalysisCard 와 동일 패턴):
 *   비로그인     → 카드 숨김
 *   매장 미등록  → 등록 유도 CTA
 *   판매 입력 X → "판매 입력 시작" CTA
 *   정상         → 3 카드 + 각 카드 클릭 시 상세 페이지
 */

interface HomeMiniTopMenu {
  menu_id: number;
  name: string;
  qty: number;
  revenue: number;
}

interface HomeMiniBep {
  bep_revenue: number;
  current_revenue: number;
  achievement_pct: number;
  gap_amount: number;
  days_passed: number;
  days_total: number;
  daily_required: number | null;
}

interface HomeMiniOut {
  store_id: number;
  store_name: string;
  has_menus: boolean;
  has_sales: boolean;
  this_week_revenue: number;
  last_week_revenue: number;
  revenue_change_pct: number | null;
  top_menu: HomeMiniTopMenu | null;
  low_stock_count: number;
  bep: HomeMiniBep | null;
}

interface StoreListOut {
  items: { id: number; name: string }[];
}

// 5/11 — 사용자 결정: ₩·만 단위 제거, 숫자+쉼표만 (33,333 형식 깔끔)
function formatKRW(v: number): string {
  return v.toLocaleString();
}

interface Props {
  /** true 시 외곽 section 래퍼 제거 — 부모 (MyStoreGroupSection 등) 가 padding/spacing 제어 */
  compact?: boolean;
}

export function StoreMiniView({ compact = false }: Props = {}) {
  const { token, isLoggedIn } = useAuthStore();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const { activeStoreId, setActiveStoreId } = useActiveStore();

  const Wrap = ({ children }: { children: React.ReactNode }) =>
    compact ? <>{children}</> : <section className="px-4 mt-6">{children}</section>;

  const [data, setData] = useState<HomeMiniOut | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<"no_store" | "fetch_error" | null>(null);

  useEffect(() => {
    if (!hasHydrated) return;
    if (!isLoggedIn || !token) {
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;
    const headers = { Authorization: `Bearer ${token}` };

    // 매장 리스트 조회 → 매장 0개 = no_store / ≥1 = 첫 매장 fetch
    const resolveAndFetch = () => {
      setLoading(true);
      axios
        .get<StoreListOut>("/api/stores", { headers })
        .then((r) => {
          if (cancelled) return;
          if (!r.data.items || r.data.items.length === 0) {
            setActiveStoreId(null);   // 옛 잔존 id 제거
            setError("no_store");
            setLoading(false);
            return;
          }
          const firstId = r.data.items[0].id;
          setActiveStoreId(firstId);
          fetchMini(firstId);
        })
        .catch(() => {
          if (!cancelled) {
            setError("fetch_error");
            setLoading(false);
          }
        });
    };

    const fetchMini = (storeId: number) => {
      setLoading(true);
      setError(null);
      axios
        .get<HomeMiniOut>(`/api/stores/${storeId}/home-mini`, { headers })
        .then((r) => {
          if (!cancelled) {
            setData(r.data);
            setLoading(false);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          // 404/403 = 옛 zustand activeStoreId 잔존 (매장 삭제 / 다른 사용자 매장)
          // → /api/stores 재조회로 복구
          const status = err?.response?.status;
          if (status === 404 || status === 403) {
            resolveAndFetch();
          } else {
            setError("fetch_error");
            setLoading(false);
          }
        });
    };

    if (activeStoreId) {
      fetchMini(activeStoreId);
    } else {
      resolveAndFetch();
    }

    return () => {
      cancelled = true;
    };
  }, [hasHydrated, isLoggedIn, token, activeStoreId, setActiveStoreId]);

  if (!hasHydrated) return null;

  // 비로그인 / 매장 미등록 — 핵심값만 blur (사용자 결정 2026-05-10 재디자인)
  // 카드 구조 그대로 노출 + 숫자만 blur → 사용자 반감 ↓ + 가입 동기 유지
  if (!isLoggedIn || error === "no_store") {
    const isAuth = isLoggedIn;
    const ctaHref = isAuth ? "/store/setup" : "/login";
    const ctaLabel = isAuth ? "🏪 매장 등록 →" : "로그인 →";
    // Phase 1.4c (2026-05-12, funnel 개선) — 락인 가치를 구체적 데이터 예시로 강화
    const lockMessage = isAuth
      ? "매장 등록 후 아래 같은 실데이터 자동 분석"
      : "로그인 + 매장 등록 후 실데이터 자동 분석";
    return (
      <Wrap>
        <div className="flex items-center justify-between mb-1">
          <h2 className="section-title mb-0">내 매장 매출 요약</h2>
          <Link href={ctaHref} className="text-xs text-primary font-semibold">{ctaLabel}</Link>
        </div>
        <FadeIn className="card">
          <p className="text-2xs text-text-sub mb-2">🔒 {lockMessage}</p>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <p className="text-2xs text-text-sub">이번 주 매출</p>
              <p className="text-base font-bold text-text mt-0.5 blur-sm select-none">142,000</p>
              <p className="text-2xs text-text-sub mt-1">지난 주 대비 ↑</p>
            </div>
            <div>
              <p className="text-2xs text-text-sub">이번 주 TOP</p>
              <p className="text-sm font-bold text-text mt-0.5 blur-sm select-none">아메리카노</p>
              <p className="text-2xs text-text-sub mt-1">N개 판매</p>
            </div>
            <div>
              <p className="text-2xs text-text-sub">재고 알림</p>
              <p className="text-base font-bold text-amber-500 mt-0.5 blur-sm select-none">3건</p>
              <p className="text-2xs text-text-sub mt-1">안전재고</p>
            </div>
          </div>
          {isAuth && (
            <p className="text-2xs text-primary mt-2.5 leading-relaxed">
              ✨ 매장 등록 = 매출·메뉴 ROI·재고·동네 갭 분석 모두 잠금 해제
            </p>
          )}
        </FadeIn>
        {/* BEP placeholder — 비로그인/매장X 케이스에도 노출 (사용자 결정 2026-05-15) */}
        <Link href={ctaHref} className="block mt-2 card hover:bg-bg-elevated transition-colors p-3">
          <div className="flex items-baseline justify-between mb-1.5">
            <p className="text-2xs text-text-sub">이번 달 손익분기점 <span className="blur-sm select-none">(██/██일)</span></p>
            <p className="text-sm font-black text-yellow-400 blur-sm select-none">███%</p>
          </div>
          <div className="h-2 bg-bg-elevated rounded-full overflow-hidden mb-1.5">
            <div className="h-full bg-yellow-400" style={{ width: "60%" }} />
          </div>
          <div className="flex items-baseline justify-between text-2xs">
            <span className="text-text-sub">
              <span className="blur-sm select-none">███,███</span> / BEP <span className="blur-sm select-none">███,███</span>원
            </span>
            <span className="text-primary font-bold">💚 흑자 +<span className="blur-sm select-none">██,███</span>원</span>
          </div>
          <p className="text-2xs text-text-sub leading-relaxed mt-1.5">
            🔒 {isAuth ? "매장 등록 + 판관비 입력 시 BEP 자동 계산" : "로그인 + 매장 등록 + 판관비 입력 시 BEP 자동 계산"}
          </p>
        </Link>
      </Wrap>
    );
  }

  if (loading || (!data && !error)) {
    return (
      <Wrap>
        <h2 className="section-title mb-3">내 매장 매출 요약</h2>
        <FadeIn className="card">
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-16 w-full" />
        </FadeIn>
      </Wrap>
    );
  }

  if (error === "fetch_error" || !data) return null;

  // 메뉴/판매 0건 — 핵심값만 blur (사용자 결정 2026-05-10 재디자인)
  if (!data.has_menus || !data.has_sales) {
    const ctaHref = !data.has_menus ? "/store/menus" : "/store/sales";
    const ctaLabel = !data.has_menus ? "메뉴 등록 →" : "판매 입력 →";
    const lockMessage = !data.has_menus
      ? "메뉴 등록하면 매출·인기 메뉴 한눈에"
      : "판매 입력하면 매출·TOP 메뉴 자동 분석";
    return (
      <Wrap>
        <div className="flex items-center justify-between mb-1">
          <h2 className="section-title mb-0">내 매장 매출 요약</h2>
          <Link href={ctaHref} className="text-xs text-primary font-semibold">{ctaLabel}</Link>
        </div>
        <FadeIn className="card">
          <p className="text-2xs text-text-sub mb-2">🔒 {lockMessage}</p>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <p className="text-2xs text-text-sub">이번 주 매출</p>
              <p className="text-base font-bold text-text mt-0.5 blur-sm select-none">142,000</p>
              <p className="text-2xs text-text-sub mt-1">지난 주 대비 ↑</p>
            </div>
            <div>
              <p className="text-2xs text-text-sub">이번 주 TOP</p>
              <p className="text-sm font-bold text-text mt-0.5 blur-sm select-none">아메리카노</p>
              <p className="text-2xs text-text-sub mt-1">N개 판매</p>
            </div>
            <div>
              <p className="text-2xs text-text-sub">재고 알림</p>
              <p className="text-base font-bold text-amber-500 mt-0.5 blur-sm select-none">3건</p>
              <p className="text-2xs text-text-sub mt-1">안전재고</p>
            </div>
          </div>
        </FadeIn>
        {/* BEP 진행바 — 부분 blur (라벨 유지, 숫자만 blur — 사용자 결정 2026-05-14) */}
        <Link
          href={ctaHref}
          className="block mt-2 card hover:bg-bg-elevated transition-colors p-3"
        >
          <div className="flex items-baseline justify-between mb-1.5">
            <p className="text-2xs text-text-sub">이번 달 손익분기점 <span className="blur-sm select-none">(██/██일)</span></p>
            <p className="text-sm font-black text-yellow-400 blur-sm select-none">███%</p>
          </div>
          <div className="h-2 bg-bg-elevated rounded-full overflow-hidden mb-1.5">
            <div className="h-full bg-yellow-400" style={{ width: "60%" }} />
          </div>
          <div className="flex items-baseline justify-between text-2xs">
            <span className="text-text-sub">
              <span className="blur-sm select-none">███,███</span> / BEP <span className="blur-sm select-none">███,███</span>원
            </span>
            <span className="text-primary font-bold">💚 흑자 +<span className="blur-sm select-none">██,███</span>원</span>
          </div>
          <p className="text-2xs text-text-sub leading-relaxed mt-1.5">
            🔒 로그인하면 본인 매장 BEP 자동 계산
          </p>
        </Link>
      </Wrap>
    );
  }

  // 정상 — 3 카드
  const change = data.revenue_change_pct;
  const changeText = change === null
    ? "지난 주 데이터 없음"
    : change > 0
      ? `↑ ${change}%`
      : change < 0
        ? `↓ ${Math.abs(change)}%`
        : "± 0%";
  const changeColor = change === null
    ? "text-text-sub"
    : change > 0
      ? "text-emerald-500"
      : change < 0
        ? "text-red-500"
        : "text-text-sub";

  return (
    <Wrap>
      <div className="flex items-center justify-between mb-1">
        <h2 className="section-title mb-0">내 매장 매출 요약</h2>
        <Link
          href="/store/dashboard"
          className="text-xs text-text-sub hover:text-text transition-colors"
        >
          상세 →
        </Link>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {/* 1. 이번 주 매출 */}
        <Link href="/store/dashboard" className="card hover:bg-bg-elevated transition-colors p-3">
          <p className="text-2xs text-text-sub mb-1">이번 주 매출</p>
          <p className="text-base font-bold text-text leading-tight">
            {formatKRW(data.this_week_revenue)}
          </p>
          <p className={`text-2xs font-semibold mt-1 ${changeColor}`}>{changeText}</p>
        </Link>

        {/* 2. TOP 메뉴 */}
        <Link href="/store/menus" className="card hover:bg-bg-elevated transition-colors p-3">
          <p className="text-2xs text-text-sub mb-1">이번 주 TOP</p>
          {data.top_menu ? (
            <>
              <p className="text-sm font-bold text-text truncate leading-tight">
                {data.top_menu.name}
              </p>
              <p className="text-2xs text-text-sub mt-1">
                {data.top_menu.qty.toLocaleString()}개 판매
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-bold text-text-sub leading-tight">—</p>
              <p className="text-2xs text-text-sub mt-1">판매 없음</p>
            </>
          )}
        </Link>

        {/* 3. 재고 알림 */}
        <Link href="/store/stock" className="card hover:bg-bg-elevated transition-colors p-3">
          <p className="text-2xs text-text-sub mb-1">재고 알림</p>
          {data.low_stock_count > 0 ? (
            <>
              <p className="text-base font-bold text-amber-500 leading-tight">
                {data.low_stock_count}건
              </p>
              <p className="text-2xs text-text-sub mt-1">안전재고 미달</p>
            </>
          ) : (
            <>
              <p className="text-base font-bold text-emerald-500 leading-tight">양호</p>
              <p className="text-2xs text-text-sub mt-1">부족 없음</p>
            </>
          )}
        </Link>
      </div>

      {/* 4. BEP 진행 (이번 달) — 데이터 있으면 진행바, 없으면 활성화 안내 (2026-05-14) */}
      {data.bep ? <BepProgressBar bep={data.bep} /> : <BepInactivePlaceholder />}
    </Wrap>
  );
}

function BepInactivePlaceholder() {
  // 사용자 결정 2026-05-15 — 판관비 미입력 케이스도 진정한 BEP 화면을 블러로 미리 보여줌
  // 옛 단순 한 줄 안내 → 진행바·숫자 blur + 입력 CTA (가입 동기 유지)
  return (
    <Link
      href="/store/dashboard"
      className="block mt-2 card hover:bg-bg-elevated transition-colors p-3"
    >
      <div className="flex items-baseline justify-between mb-1.5">
        <p className="text-2xs text-text-sub">이번 달 손익분기점 <span className="blur-sm select-none">(██/██일)</span></p>
        <p className="text-sm font-black text-yellow-400 blur-sm select-none">███%</p>
      </div>
      <div className="h-2 bg-bg-elevated rounded-full overflow-hidden mb-1.5">
        <div className="h-full bg-yellow-400" style={{ width: "60%" }} />
      </div>
      <div className="flex items-baseline justify-between text-2xs">
        <span className="text-text-sub">
          <span className="blur-sm select-none">███,███</span> / BEP <span className="blur-sm select-none">███,███</span>원
        </span>
        <span className="text-primary font-bold">💚 흑자 +<span className="blur-sm select-none">██,███</span>원</span>
      </div>
      <p className="text-2xs text-text-sub leading-relaxed mt-1.5">
        🔒 <b className="text-text">판관비</b>(임대료·인건비 등) 입력하면 위 숫자 자동 계산
        <span className="text-primary font-semibold ml-1">→ 입력하러 가기</span>
      </p>
    </Link>
  );
}

function BepProgressBar({ bep }: { bep: HomeMiniBep }) {
  const achieved = bep.achievement_pct >= 100;
  const remaining = bep.days_total - bep.days_passed;
  return (
    <Link
      href="/store/dashboard"
      className="block mt-2 card hover:bg-bg-elevated transition-colors p-3"
    >
      <div className="flex items-baseline justify-between mb-1.5">
        <p className="text-2xs text-text-sub">
          이번 달 손익분기점
          <span className="ml-1.5">({bep.days_passed}/{bep.days_total}일)</span>
        </p>
        <p className={`text-sm font-black ${achieved ? "text-primary" : "text-yellow-400"}`}>
          {bep.achievement_pct.toFixed(0)}%
        </p>
      </div>
      <div className="h-2 bg-bg-elevated rounded-full overflow-hidden mb-1.5">
        <div
          className={`h-full transition-all ${achieved ? "bg-primary" : "bg-yellow-400"}`}
          style={{ width: `${Math.min(bep.achievement_pct, 100)}%` }}
        />
      </div>
      <div className="flex items-baseline justify-between text-2xs">
        <span className="text-text-sub">
          {formatKRW(bep.current_revenue)} / BEP {formatKRW(bep.bep_revenue)}원
        </span>
        {achieved ? (
          <span className="text-primary font-bold">💚 흑자 +{formatKRW(-bep.gap_amount)}원</span>
        ) : bep.daily_required && remaining > 0 ? (
          <span className="text-yellow-400 font-semibold">
            남은 {remaining}일 → 일 {formatKRW(bep.daily_required)}원 필요
          </span>
        ) : (
          <span className="text-yellow-400 font-semibold">{formatKRW(bep.gap_amount)}원 부족</span>
        )}
      </div>
    </Link>
  );
}
